import type {
  CartBuyerIdentityInput,
  CartCreateInput,
  CartLineInput,
  CartLineUpdateInput,
  ShopifyAttribute,
  ShopifyCart
} from '@petplate/utils/shopify/types';
import { createContext, useContext } from 'react';

export enum CartStatus {
  Uninitialized = 'uninitialized',
  Fetching = 'fetching',
  Creating = 'creating',
  Updating = 'updating',
  Idle = 'idle'
}

export type CartValues = {
  cartId?: string;
  lines: ShopifyCart['lines']['nodes'];
  buyerIdentity?: ShopifyCart['buyerIdentity'];
  attributes: ShopifyCart['attributes'];
  discountCodes: ShopifyCart['discountCodes'];
  cost?: ShopifyCart['cost'];
  checkoutUrl?: string;
  note?: string;
  status: CartStatus;
  cartReady: boolean;
};

export type CartActions = {
  cartCreate: (input: CartCreateInput) => Promise<string | undefined>;
  cartClear: () => Promise<void>;
  linesAdd: (lines: CartLineInput[]) => Promise<void>;
  linesUpdate: (lines: CartLineUpdateInput[]) => Promise<void>;
  linesRemove: (lines: string[]) => Promise<void>;
  noteUpdate: (note: string) => Promise<void>;
  buyerIdentityUpdate: (buyerIdentity: CartBuyerIdentityInput) => Promise<void>;
  attributesUpdate: (attributes: ShopifyAttribute[]) => Promise<void>;
  discountCodesUpdate: (discountCodes: string[]) => Promise<void>;
};

export interface CartContextProps extends CartValues, CartActions {}

export const CartContext = createContext<CartContextProps | null>(null);

export const useShopifyCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('Shopify Cart context not initialized.');
  return context;
};
